//Library Imports
import { Typography } from "antd";
import { FC, useMemo, useState } from "react";
import store, { RootState } from "store";
import { useSelector } from "react-redux";
import React from "react";

//Local Imports
import TableSort from "modules/Common/Components/TableSort";
import { getDeliveredContactsList } from "./action";
import Speed from "../../../assets/img/icons/Speed.svg";
import { CONTACT_ANALYTICS } from "constants/types";
import { getPagination, RedirectToContactDetails } from "./utils";
import { setSortDirection } from "modules/Analytics/utils";
import ContactLoader from "./Loaders/ContactLoader";
import BlankProfilePhoto from "assets/img/blank_profile_photo.png";

//CSS Imports
import styled from "styled-components";
import { addCommaToNumber } from "utils";
interface IDeliveredContactsList {
  page?: string;
  tab?: string;
  searchedText: string;
  setSearchedText: (value: string) => void;
}

const DeliveredContactsList: FC<IDeliveredContactsList> = (props: IDeliveredContactsList) => {
  //props constants
  const { searchedText, setSearchedText } = props;

  //Local States
  const [sortType, setSortType] = useState({ accessor: "", sortDirection: "none" });
  const [sortBy, setSortBy] = useState("");

  //Redux states
  const delivered = useSelector((state: RootState) => state.contactAnalytics.delivered);
  const loading = useSelector((state: RootState) => state.contactAnalytics.deliveredLoading);
  const total_delivered = useSelector((state: RootState) => state.contactAnalytics.total_delivered);
  const contactsByFilter = useSelector((state: RootState) => state.contactAnalytics.contactsByFilter);

  const AllCampaignsFilterCheck = useSelector(
    (state: RootState) => state.analyticsDashboard.appliedCampaignsFilters.isAllCampaigns
  );

  const { Text, Paragraph } = Typography;
  const [ellipsis, setEllipsis] = React.useState(true);

  let columnsData = useMemo(
    () => [
      {
        id: "main",
        hideHeader: true,
        disableSortBy: true,
        columns: [
          {
            id: "contact_name",
            Header: "Contact",
            accessor: "contact_name",
            disableSortBy: true,
            Cell: (props: any) => {
              let item = props.row.original;
              let name =
                item?.contact_name === null || (item.contact_email !== null && item.contact_name?.trim() === "")
                  ? item.contact_email
                  : item.contact_name;

              return (
                <div>
                  <div className="preview">
                    <img
                      style={{
                        boxShadow: item.personality.color ? `0px 0px 0px 2px ${item.personality.color}` : "",
                      }}
                      src={
                        item.contact_image !== null && item.contact_image !== ""
                          ? item.contact_image
                          : BlankProfilePhoto
                      }
                      alt=""
                      onError={(e: any) => {
                        e.target.onerror = null;
                      }}
                    />
                  </div>
                  <div className="details">
                    <>
                      <div className="name">
                        <Text ellipsis={ellipsis ? { rows: 1, tooltip: name } : false}>{name}</Text>
                      </div>
                      <div className="company">
                        <Text ellipsis={ellipsis ? { rows: 1, tooltip: item.contact_email } : false}>
                          {item.contact_email}
                        </Text>
                      </div>
                      <div className="designation">
                        <Text ellipsis={ellipsis ? { rows: 1, tooltip: item.job_title } : false}>
                          {item.job_title}
                        </Text>
                      </div>
                    </>
                  </div>
                </div>
              );
            },
          },
          {
            id: "company_name",
            Header: "Company",
            accessor: "company_name",
            disableSortBy: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return <div className="company-name">{item.company_name}</div>;
            },
          },
          {
            id: "personality",
            Header: "Personality",
            disableSortBy: true,
            accessor: "personality.archetype",
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <span className="organization d-flex align-items-center">
                  <Circle color={item.personality.color ? item.personality.color : "black"} />
                  {item.personality.archetype === undefined ? "N/A" : item.personality.archetype}
                </span>
              );
            },
          },
        ],
      },
      {
        disableSortBy: true,
        Header: "Opens",
        columns: [
          {
            id: "total_opens",
            Header: "Total",
            accessor: "total_opens",
            sortType: "basic",
            sortDirection: sortType.accessor === "total_opens" ? sortType.sortDirection : "none",
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber((item.total_opens ?? 0)?.toString() ?? "");
            },
          },
          {
            id: "unique_opens",
            Header: "Unique",
            accessor: "unique_opens",
            sortType: "basic",
            sortDirection: sortType.accessor === "unique_opens" ? sortType.sortDirection : "none",
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber((item.unique_opens ?? 0)?.toString() ?? "");
            },
          },
        ],
      },
      {
        disableSortBy: true,
        Header: "Clicks",
        columns: [
          {
            id: "total_clicks",
            Header: "Total",
            accessor: "total_clicks",
            sortType: "basic",
            sortDirection: sortType.accessor === "total_clicks" ? sortType.sortDirection : "none",
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber((item.total_clicks ?? 0)?.toString() ?? "");
            },
          },
          {
            id: "unique_clicks",
            Header: "Unique",
            accessor: "unique_clicks",
            sortType: "basic",
            sortDirection: sortType.accessor === "unique_clicks" ? sortType.sortDirection : "none",
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return addCommaToNumber((item.unique_clicks ?? 0)?.toString() ?? "");
            },
          },
        ],
      },
      {
        disableSortBy: true,
        Header: "Score",
        columns: [
          {
            id: "campaign_score",
            Header: "Campaign(s)",
            accessor: "campaigns_score",
            sortType: "basic",
            sortDirection: sortType.accessor === "campaign_score" ? sortType.sortDirection : "none",
            canSort: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div className="last-item">
                  <div className="engagement">
                    <img src={Speed} alt="" />
                    &nbsp;
                    {addCommaToNumber((item.campaigns_score ?? 0)?.toString() ?? "")}
                  </div>
                </div>
              );
            },
          },
          {
            id: "lifetime_score",
            Header: "Lifetime",
            accessor: "lifetime_score",
            disableSortBy: true,
            Cell: (props: any) => {
              let item = props.row.original;
              return (
                <div className="last-item">
                  <div className="engagement">
                    <img src={Speed} alt="" />
                    &nbsp;
                    {addCommaToNumber((item.lifetime_score ?? 0)?.toString() ?? "")}
                  </div>
                </div>
              );
            },
          },
        ],
      },
    ],
    [sortType]
  );

  const sortedResults = (column?: any) => {
    let sort_type = "";
    if (column.canSort) {
      setSortBy(column.id);
      sort_type = setSortDirection(column.id, column.sortDirection);
      setSortType({ accessor: column.id, sortDirection: sort_type });
      store.dispatch({
        type: CONTACT_ANALYTICS.SET_SORT_PROPS,
        payload: { sortBy: column.id, sortType: sort_type },
      });
      store.dispatch({
        type: CONTACT_ANALYTICS.DELIVERED_RESET,
      });
      getDeliveredContactsList(getParams({ sortType: sort_type, sortBy: column.id }));
    }
  };

  const getParams = (params?: any) => {
    return getPagination("delivered", {
      allCampaigns: AllCampaignsFilterCheck,
      searchWord: searchedText,
      offset: params !== undefined ? 0 : delivered.length > 0 ? delivered.length : 0,
      sortBy: params !== undefined ? params.sortBy : sortBy,
      sortType: params !== undefined ? params.sortType : sortType.sortDirection,
      ...(contactsByFilter !== "" && { contactsByFilter: contactsByFilter }),
    });
  };

  return (
    <Wrapper>
      {delivered !== undefined ? (
        <>
          <TableSort
            columns={columnsData}
            data={delivered}
            module={"delivered"}
            _onCellClick={sortedResults}
            _onRowClick={RedirectToContactDetails}
            hasMore={(loading && delivered.length === 0) || delivered?.length < total_delivered}
            // hasMore={true}
            next={() => getDeliveredContactsList(getParams())}
            loader={[1, 2, 3].map((item) => (
              <ContactLoader key={item} loading={loading} />
            ))}
            useCustomQuery
          />
          {!loading && delivered?.length === 0 ? <div className="no-data">No Data Found!</div> : null}
        </>
      ) : (
        <div className="no-data">No Data Found!</div>
      )}
    </Wrapper>
  );
};

export default DeliveredContactsList;

const Circle = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  background: ${(props) => props.color};
  border-radius: 50%;
  margin-right: 3px;
`;

const Wrapper = styled.div`
  /* border-radius: 10px; */
  /* overflow: hidden; */
  width: 100%;
  margin-bottom: 10px;
  /* padding: 14px 13px 14px 18px; */
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 16px;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    /* border-radius: 7px; */
    /* padding: 10px 12px; */
    min-height: 83px;
    margin-top: 10px;
  }
  > div {
    width: 100%;
  }
  svg {
    rect.personality {
      x: 46%;
      width: 7%;
      @media (max-width: 1621px) {
        x: 33%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 38%;
        width: 9%;
      }
    }
    rect.company {
      x: 26%;
      width: 5%;
      @media (max-width: 1621px) {
        x: 21%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 28%;
        width: 7%;
      }
    }
    rect.totalOpen {
      x: 63%;
      width: 3%;
      @media (max-width: 1621px) {
        x: 46%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 50%;
        width: 3%;
      }
    }
    rect.uniqueOpen {
      x: 69%;
      width: 3%;
      @media (max-width: 1621px) {
        x: 55%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 57%;
        width: 3%;
      }
    }
    rect.totalClick {
      x: 75%;
      width: 3%;
      @media (max-width: 1621px) {
        x: 64%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 64%;
        width: 3%;
      }
    }
    rect.uniqueClick {
      x: 81%;
      width: 3%;
      @media (max-width: 1621px) {
        x: 73%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 71%;
        width: 3%;
      }
    }

    rect.campaignScore {
      x: 87%;
      width: 5%;
      @media (max-width: 1621px) {
        x: 81%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 78%;
        width: 8%;
      }
    }

    rect.lifetimeScore {
      x: 94%;
      width: 5%;
      @media (max-width: 1621px) {
        x: 91%;
      }
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        x: 90%;
        width: 8%;
      }
    }
  }

  .no-data {
    position: absolute;
    top: 50%;
    width: 100%;
    height: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: "robotomedium";
    color: #67696a;
    font-size: 1rem;
    margin-bottom: 30px;
    /* box-shadow: 6px 30px 73px -10px grey; */
    border-radius: 5px;
  }
  > div {
    width: 100%;
  }
  table {
    width: 100%;
    margin-top: 10px;
    margin-bottom: 20px;
    thead {
      border-bottom: 1px solid #dddddd;
      tr {
        th {
          color: #5e7079;
          font-weight: 700;
          font-size: 18px;
          font-family: "Roboto", sans-serif;
        }
      }
    }
    tbody {
      width: 100%;
      display: block;
      tr {
        background-color: #ffffff;
        box-shadow: 0px 0px 17px -2px rgba(0, 0, 0, 0.08);
        width: 100%;
        border-radius: 8px;
        transition-duration: 0.2s;
        position: relative;
        margin: 4px 0;
        padding: 16px 15px 10px 15px;
        cursor: pointer;
        @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
          padding-right: 0px;
        }
      }
    }
    thead {
      border-bottom: 1px solid #dddddd;
      tr {
        padding: 0 15px;
        &:before {
          max-height: 50px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            max-height: 38px;
          }
        }
      }
      th {
        color: #5e7079;
        font-weight: 700;
        font-size: 18px;
        font-family: "Roboto", sans-serif;
        white-space: nowrap;
        span {
          width: 15px;
          .sort_asc {
            width: 10px;
            height: 8px;
            margin-top: -15px;
            margin-left: 5px;
            float: left;
            @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
              margin-top: -13px;
            }
            img {
              transform: rotate(180deg);
            }
          }
          .sort_desc {
            width: 10px;
            height: 8px;
            float: left;
            margin-left: 5px;
            margin-top: 5px;
            img {
              float: left;
            }
          }
        }
      }
      tr:nth-child(1) {
        justify-content: end;
        display: flex;
        th {
          font-size: 18px;
          font-weight: 700;
          color: #5e7079;
          font-family: "Roboto", sans-serif;
          justify-content: center;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
          }
          &:nth-child(2) {
            width: 200px;
            @media all and (max-width: 1439px) {
              width: 110px;
            }
          }
          &:nth-child(3) {
            width: 200px;
            @media all and (max-width: 1439px) {
              width: 110px;
            }
          }
          &:nth-child(4) {
            width: 240px;
            @media all and (max-width: 1439px) {
              width: 200px;
            }
          }
        }
      }
      tr:nth-child(2) {
        th {
          font-size: 16px;
          color: #5e7079;
          font-weight: 500;
          font-family: "Roboto", sans-serif;
          white-space: nowrap;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
          }
          &:nth-child(1) {
            padding-left: 55px;
          }
        }
      }
    }
    tbody {
      width: 100%;
      display: block;
      box-sizing: border-box;
      overflow-y: auto;
      height: calc(100vh - 360px);
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        height: calc(100vh - 300px);
      }
      scrollbar-width: thin;
      scrollbar-color: #001760 #efefef;
      &::-webkit-scrollbar {
        width: 5px;
        height: 5px;
        border-radius: 5px;
      }
      /* Track */
      &::-webkit-scrollbar-track {
        background: #fff;
        margin-top: 0;
        margin-bottom: 10px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          margin-top: 0;
        }
      }
      /* Handle */
      &::-webkit-scrollbar-thumb {
        background: #001760;
      }
      /* Handle on hover */
      /* &::-webkit-scrollbar-thumb:hover {
        background: #efefef;
      } */
      > div {
        overflow: hidden;
      }
      tr {
        background-color: #ffffff;
        box-shadow: 0px 0px 17px -2px rgba(0, 0, 0, 0.08);
        width: 100%;
        border-radius: 8px;
        transition-duration: 0.2s;
        position: relative;
        margin: 4px 0;
        padding: 16px 15px 10px 15px;
        cursor: pointer;
      }
    }
    thead,
    tbody {
      width: 100%;
      display: block;
      float: left;
      tr:nth-child(1) {
        th {
          &:nth-child(1) {
            @media all and (max-width: 1439px) {
              //Safari (7.1+)
              _::-webkit-full-page-media,
              _:future,
              :root .safari_only {
                width: calc(100% - 535px) !important;
              }
              //Safari (from 6.1 to 10)
              @media screen and (min-color-index: 0) and(-webkit-min-device-pixel-ratio:0) {
                @media {
                  width: calc(100% - 535px) !important;
                }
              }
              //Safari (10.1+)
              @media not all and (min-resolution: 0.001dpcm) {
                @media {
                  width: calc(100% - 535px) !important;
                }
              }
            }
          }
        }
      }
      tr {
        width: 100%;
        display: block;
        float: left;
        td {
          min-height: 70px;
          font-family: "Roboto", sans-serif;
          font-weight: 500;
          font-size: 20px !important;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px !important;
          }
        }
        th,
        td {
          display: block;
          float: left;
          &[role="cell"] {
            color: #424b50;
          }
          &:nth-child(1) {
            width: calc(80% - 890px);
            text-align: left;
            @media all and (max-width: 1621px) {
              width: calc(90% - 780px);
            }
            @media all and (max-width: 1439px) {
              width: calc(100% - 620px);
            }

            > div {
              display: flex;
              width: 100%;
              align-items: center;
              flex-direction: row;
              min-height: 65px;
              .preview {
                float: left;
              }
              .details {
                float: left;
                font-weight: 400;
                color: #000000;
                font-size: 20px;
                font-family: "Roboto Slab", serif;
                overflow: hidden;
                @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                  font-size: 14px;
                }
                width: 100%;
                .name {
                  font-weight: 400;
                  color: #000000;
                  font-size: 20px;
                  font-family: "Roboto Slab", serif;
                  width: 100%;
                  @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                    line-height: 24px;
                  }
                  .ant-typography {
                    margin: 0;
                    max-width: 100%;
                    width: 100%;
                  }
                }
                .company {
                  color: #1ec252;
                  font-weight: 500;
                  font-size: 14px;
                  font-family: "Roboto", sans-serif;
                  display: block;
                  @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                    font-size: 12px;
                  }
                  .ant-typography {
                    margin: 0;
                    max-width: 100%;
                    color: #1ec252;
                    font-weight: 500;
                    font-size: 14px;
                  }
                }
                .designation {
                  color: #424b50;
                  font-weight: 500;
                  font-size: 14px;
                  font-family: "Roboto", sans-serif;
                  display: block;
                  @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                    font-size: 12px;
                  }
                  .ant-typography {
                    margin: 0;
                    max-width: 100%;
                  }
                }
              }
            }
          }
          &:nth-child(2) {
            width: 20%;
            text-align: left;
            display: inline-flex;
            align-items: center;
            .company-name {
              display: -webkit-box;
              -webkit-line-clamp: 3;
              -webkit-box-orient: vertical;
              overflow: hidden;
              text-overflow: ellipsis;
              word-break: break-word;
              color: #424b50;
              font-size: 16px;
              @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                font-size: 12px;
              }
              @media all and (max-width: 1125px) {
                padding-left: 12px;
              }
            }
            @media all and (max-width: 1621px) {
              width: 10%;
            }
            @media all and (max-width: 1439px) {
              width: 100px;
            }
          }
          &:nth-child(3) {
            width: 250px;
            text-align: left;
            display: inline-flex;
            align-items: center;
            @media all and (max-width: 1621px) {
              width: 140px;
            }
            @media all and (max-width: 1439px) {
              width: 90px;
            }
          }
          &:nth-child(4) {
            width: 100px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 60px;
            }
          }
          &:nth-child(5) {
            width: 100px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 60px;
            }
          }
          &:nth-child(6) {
            width: 100px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 60px;
            }
          }
          &:nth-child(7) {
            width: 100px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 60px;
            }
          }
          &:nth-child(8) {
            width: 120px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 100px;
            }
          }
          &:nth-child(9) {
            width: 120px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 90px;
            }
          }
        }
      }
    }
  }
  .engagement {
    border-radius: 12px;
    background-color: #1971eb;
    color: #ffffff;
    font-size: 16px;
    font-family: "robotomedium";
    font-weight: 500;
    padding: 0px 10px;
    display: inline;
    float: right;
    white-space: nowrap;
    @media all and (max-width: 1439px) {
      font-size: 12px;
       padding: 4px 8px;
    }
  }
  .blog-image {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .blog-image {
    .img1 {
      height: 70px;
      width: 52px;
      border-radius: 6px 0px 0px 6px;
      margin-right: 2px;
      @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
        border-radius: 4px 0px 0px 4px;
        height: 50px;
        width: 36px;
      }
    }
    .img-bg {
      background-position: center center;
      background-size: cover;
      background-repeat: no-repeat;
    }
    .right-images {
      .overlayed-image {
        position: absolute;
        left: 9%;
        top: 22%;
      }
      .img-bg {
        background-position: center center;
        background-size: cover;
        background-repeat: no-repeat;
      }
      display: flex;
      flex-direction: column;
      .img2 {
        height: 34px;
        width: 39px;
        border-radius: 0px 6px 0px 0px;
        margin-bottom: 1px;
        @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
          border-radius: 0px 4px 0px 0px;
          height: 25px;
          width: 28px;
        }
      }
      .img3 {
        height: 34px;
        width: 39px;
        border-radius: 0px 0px 6px 0px;
        @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
          border-radius: 0px 0px 4px 0px;
          height: 23px;
          width: 28px;
        }
      }
    }
  }
  .disabled {
    pointer-events: none !important;
    background-color: rgba(225, 225, 225, 0.7) !important;
    cursor: no-drop;
  }
  .preview {
    position: relative;
    max-width: 55px;
    -ms-flex-negative: 1 !important;
    flex-shrink: 1 !important;
    img {
      border-radius: 100%;
      max-width: 100%;
      height: 55px;
      width: 55px;
      min-width: 55px;
      outline: none;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        height: 40px;
        width: 40px;
        min-width: 40px;
      }
    }
  }
  .details {
    padding-left: 15px;
    width: 75%;
    .name,
    .company,
    .designation,
    .recipient-count {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 0px;
      -webkit-box-align: start;
      width: 100%;
      word-break: break-word;
      .ant-typography-ellipsis-single-line {
        width: 100%;
      }
    }
    .name {
      color: #000000;
      font-size: 20px;
      font-weight: 400;
      line-height: 25px;
      font-family: "Roboto Slab", serif;
      display: block;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 15px;
        font-size: 13px;
      }
      .ant-typography-ellipsis-single-line {
        width: 100%;
      }
    }
    .list-name {
      color: #000000;
      font-size: 20px;
      font-weight: 400;
      line-height: 22px;
      font-family: "Roboto Slab", serif;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-word;

      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 14px;
        font-size: 13px;
      }
    }
    .company {
      -webkit-line-clamp: 1;
      font-family: "Roboto", sans-serif;
      font-weight: 500;
      font-size: 14px;
      line-height: 16px;
      color: #424b50;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 18px;
        font-size: 9px;
      }
      .ant-typography {
        margin: 0;
        max-width: 100%;
        color: #1ec252;
        font-weight: 500;
        font-size: 14px;
      }
    }
    .designation {
      -webkit-line-clamp: 1;
      font-family: "Roboto", sans-serif;
      font-weight: 500;
      line-height: 16px;
      font-size: 14px;
      color: #27ce0a;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 18px;
        font-size: 9px;
      }
    }
    .recipient-count {
      -webkit-line-clamp: 1;
      font-family: "Roboto", sans-serif;
      font-weight: 500;
      line-height: 16px;
      font-size: 14px;
      color: #27ce0a;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        line-height: 18px;
        font-size: 9px;
      }
    }
  }
  .actions {
    -ms-flex-negative: 1;
    flex-shrink: 1;
    align-self: center;
    margin-left: auto;
    min-width: 106px;
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      width: 100%;
      li {
        float: left;
        display: inline-block;
        padding: 0px;
        &.clicks {
          padding-right: 10px;
          .container-span {
            font-family: "Roboto", sans-serif;
            color: #fff;
            font-size: 16px;
            font-weight: 500;
            background-color: #1971eb;
            border-radius: 12px;
            padding: 0px 10px;
            display: inline-flex;
            width: 80px;
            height: 22px;
            justify-content: center;
            align-items: center;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 11px;
              width: 70px;
            }
            svg {
              vertical-align: baseline;
              margin-right: 5px;
            }
          }
        }
        &.card-popover {
          a {
            padding: 0 5px;
            svg {
              width: 5px;
              max-height: none;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                width: 3px;
              }
            }
          }
        }
      }
    }
  }
`;
